import React, { useState, useEffect, useContext } from "react";

import PropTypes from "prop-types";
import clsx from "clsx";
import { lighten, makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Paper,
  Checkbox,
  Tooltip,
  Box,
  Collapse,
  IconButton,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";

import { CustomTable, CustomContainer } from "./styles/BigDataTable";
import { find } from "../../repositories/qerpapi/erp/RouteRepository";

import { QueryParamsContext } from "../../context/QueryParamsContext";

function descendingComparator(a, b, orderBy) {
  // Converte para número (se possível), antes de ordenar
  const valor1 = Number.isNaN(Number(a[orderBy]))
    ? a[orderBy]
    : Number(a[orderBy]);

  const valor2 = Number.isNaN(Number(b[orderBy]))
    ? b[orderBy]
    : Number(b[orderBy]);

  if (valor2 < valor1) {
    return -1;
  } else if (valor2 > valor1) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
  const {
    classes,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    columns,
    selectable,
    subitemTable,
    multiSelect,
    disabledTab,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {selectable && (
          <TableCell padding="checkbox">
            {multiSelect && (
              <Checkbox
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
                inputProps={{ "aria-label": "Selecione todos os itens" }}
                disabled={!multiSelect}
              />
            )}
          </TableCell>
        )}
        {subitemTable && <TableCell></TableCell>}
        {columns.map((headCell, index) => {
          return (
            <TableCell
              key={headCell.id ?? index}
              align={headCell.align}
              padding={headCell.disablePadding ? "none" : "normal"}
              style={headCell.style || {}}
            >
              <Tooltip
                title={headCell.description ?? ""}
                aria-label={headCell.description ?? ""}
                tabIndex={disabledTab ? "-1" : "0"}
              >
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                  disabled={headCell.disabled}
                >
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </Tooltip>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: "1 1 100%",
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();
  const { numSelected } = props;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      {numSelected > 0 ? (
        <Typography
          className={classes.title}
          color="inherit"
          variant="h6"
          component="div"
        >
          {numSelected > 1
            ? `${numSelected} itens selecionados`
            : `${numSelected} item selecionado`}
        </Typography>
      ) : (
        <Typography
          className={classes.title}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          {props.tableTitle}
        </Typography>
      )}
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

export default function BigDataTable({
  paddingNoneRows = false,
  rowsPerRequest = "50",
  fields,
  reload,
  route,
  where = null,
  whereTranslate = null,
  include = "",
  orWhere = null,
  orWhereTranslate = null,
  between = "",
  disabledDefaultPagination = false,
  stickyHeader = false,
  disabledTab = false,
  maxHeight = "",
  subitemTable = false,
  columns = [],
  rowsPerPageOptions = [],
  rowsPerPage = 10,
  rowId = "id",
  tableTitle = "",
  selectable = false,
  selected = [],
  quantidadeItensTotal,
  setSelected = () => {},
  setInitialPage = () => {},
  initialPage = 0,
  initialSize = 33,
  multiSelect = true,
  initialSort = "",
  initialOrder = "asc",
  fixedSort = "",
  selectAllOnLoad = false,
  urlObjects = null,
  rawUrl = "",
  initialFunction = (data) => data,
  allSelected = false,
  setAllSelected = () => {},
  ...props
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(-1);
  const [order, setOrder] = useState(initialOrder);
  const [orderBy, setOrderBy] = useState(initialSort);
  const [page, setPage] = useState(initialPage);
  const [rowsPerPageDefault, setRowsPerPageDefault] = useState(rowsPerPage);

  const [apiTotalRows, setApiTotalRows] = useState(0);
  const [apiPagination, setApiPagination] = useState(1);
  const [apiLastPage, setApiLastPage] = useState(0);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState(fixedSort);

  const { queryParams } = useContext(QueryParamsContext);

  const getValuesByPagination = async (apiPaginaton) => {
    try {
      setLoading(true);

      const callback = await find({
        empresaCnpj: queryParams.empresa_cnpj,
        path: route,
        include,
        orWhere,
        orWhereTranslate,
        where,
        whereTranslate,
        perPage: rowsPerRequest,
        page: apiPaginaton,
        sort,
        fields,
        between,
        urlObjects,
        rawUrl,
      });

      if (callback?.success) {
        initialFunction(callback.data).forEach((value) => values.push(value));

        if (allSelected) {
          setSelected(values);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const getData = async () => {
      try {
        setSelected([]);

        // reset da tabela
        setLoading(true);
        setValues([]);
        setApiTotalRows(0);
        setApiPagination(1);
        setApiLastPage(0);
        setPage(0);

        // todo: bloquear botao de paginacao enquanto estiver fazendo requisicao

        const callback = await find({
          empresaCnpj: queryParams.empresa_cnpj,
          path: route,
          include,
          orWhere,
          orWhereTranslate,
          where,
          whereTranslate,
          perPage: rowsPerRequest,
          page: 1,
          sort,
          fields,
          between,
          urlObjects,
          rawUrl,
        });

        if (callback && callback.success) {
          const valuesInitialFunction = initialFunction(callback.data);
          setValues(valuesInitialFunction);
          setApiTotalRows(
            callback.total > valuesInitialFunction.length
              ? callback.total
              : valuesInitialFunction.length
          );
          setApiLastPage(callback.last_page);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    getData();
    // eslint-disable-next-line
  }, [
    queryParams.empresa_cnpj,
    sort,
    orWhere,
    orWhereTranslate,
    where,
    whereTranslate,
    reload,
    between,
    urlObjects,
    rawUrl,
  ]);

  useEffect(() => {
    setAllSelected(selectAllOnLoad);
    if (!selectAllOnLoad) return;

    setSelected([...values]);
  }, [selectAllOnLoad, values, setSelected, setAllSelected]);

  // Alertas para o desenvolvedor
  if (!columns || columns.length === 0) {
    alert("É necessário informar a propriedade columns no Grid");
    return <></>;
  } else if (selectable && !setSelected && !selected) {
    alert(
      "É necessário informar as propriedades selected e setSelected, quando o grid for selecionável"
    );
    return <></>;
  }

  let columnIds = [];
  let rowFormat = [];
  let rowAlign = [];
  // Adiciona os campos padrões nas colunas (caso não tenham sido preenchidos)
  columns.forEach((coluna) => {
    // Padrão de alinhamento para a esquerda (caso não tenha sido definido - Numérico para a direita)
    if (!coluna.align) coluna.align = coluna.numeric ? "right" : "left";
    rowAlign.push(coluna.align);
    // Formatação de dígitos
    rowFormat.push(coluna.format);
    coluna.disablePadding = coluna.disablePadding
      ? coluna.disablePadding
      : false;
    coluna.id = coluna.id ? coluna.id : coluna.label;
    columnIds.push(coluna.id);
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);

    // reset da table
    setLoading(true);
    setValues([]);
    setPage(0);
    setApiTotalRows(0);
    setApiPagination(1);

    setSort(
      fixedSort
        ? `${fixedSort},${isAsc ? "-" : ""}${property}`
        : `${isAsc ? "-" : ""}${property}`
    );
  };

  // Adiciona todos os objetos dentro do selected
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = values.map((n) => n);
      setSelected(newSelecteds);
    } else {
      setSelected([]);
    }

    setAllSelected(event.target.checked);
    return null;
  };

  // Adiciona/remove o selected de acordo com o rowId identificado
  const handleClick = (event, item) => {
    if (!selectable) {
      return;
    }

    if (!multiSelect) {
      setSelected([item]);
      setAllSelected(false);
      return;
    }

    let selectedIndex = -1;

    // Verifica o index do item, caso não encontre, manda o -1 (adiciona um item novo)
    selected.forEach((select, index) => {
      if (select[rowId] === item[rowId]) {
        selectedIndex = index;
        return;
      }
    });

    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, item);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
    setAllSelected(newSelected.length === values.length);
  };

  const handleChangePage = async (event, newPage) => {
    if (
      (!values || values.length <= newPage * rowsPerPage) &&
      apiPagination < apiLastPage
    ) {
      let page = apiPagination;

      getValuesByPagination(page + 1);
      setApiPagination((old) => old + 1);
    }

    setPage(newPage);
    setInitialPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPageDefault(parseInt(event.target.value, 10));
    setPage(0);
    setInitialPage(0);
  };

  const isSelected = (item) => {
    let retorno = false;
    selected.forEach((select) => {
      if (select[rowId] === item[rowId]) {
        retorno = true;
      }
    });
    return retorno;
  };

  // Calculo para saber quantos itens estão mostrando no momento
  let qtdPaginaAtual = values.length - page * rowsPerPageDefault;

  const emptyRows =
    rowsPerPageDefault - Math.min(rowsPerPageDefault, qtdPaginaAtual);

  return (
    <CustomTable>
      <Paper className={classes.paper}>
        {tableTitle && (
          <EnhancedTableToolbar
            numSelected={selected.length}
            tableTitle={tableTitle}
          />
        )}
        <CustomContainer style={{ maxHeight: `${maxHeight}` }}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={"small"}
            aria-label="enhanced table"
            stickyHeader={stickyHeader}
          >
            <EnhancedTableHead
              columns={columns}
              align={rowAlign}
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              selectable={selectable}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={values.length}
              subitemTable={subitemTable}
              multiSelect={multiSelect}
              disabledTab={disabledTab}
            />
            <TableBody>
              {
                // Adiciona o loading, caso seja necessário
                loading
                  ? (function loading() {
                      let minRows = [];
                      for (let row = 0; row < rowsPerPageDefault; row++) {
                        minRows.push(
                          <TableRow
                            role="checkbox"
                            tabIndex={-1}
                            key={`TableRowLoading_${row}`}
                          >
                            {selectable && (
                              <TableCell
                                padding="checkbox"
                                key={`skeleton_${row}`}
                              >
                                <Skeleton variant="rect" animation="wave" />
                              </TableCell>
                            )}
                            {columnIds.map((value, index) => {
                              return (
                                <TableCell key={`skeleton_${row}_${index}`}>
                                  <Skeleton variant="text" animation="wave" />
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      }
                      return minRows;
                    })()
                  : // Adiciona as linhas da tabela, depois de ficar carregado
                    stableSort(values, getComparator(order, orderBy))
                      // Se for customPagination, a pesquisa deve ser feita fora da table,
                      // e sempre será retornado a quantidade de itens aqui.
                      // Se não for customPagination, dá uma slice para retornar somente a quantidade definida.
                      .slice(
                        page * rowsPerPageDefault,

                        page * rowsPerPageDefault + rowsPerPageDefault
                      )
                      .map((row, index) => {
                        const isItemSelected = isSelected(row);
                        const labelId = `enhanced-table-checkbox-${index}`;
                        // Adiciona os itens da tabela
                        return (
                          <React.Fragment key={index}>
                            <TableRow
                              style={{
                                // Remove o background em caso de item selecionado
                                backgroundColor: isItemSelected
                                  ? null
                                  : row["backgroundColor"] ||
                                    (row["rowError"] && "#ffcfc7"),
                              }}
                              hover
                              onClick={(event) => handleClick(event, row)}
                              role="checkbox"
                              aria-checked={isItemSelected}
                              tabIndex={-1}
                              selected={isItemSelected}
                            >
                              {subitemTable && (
                                <TableCell style={{ width: 20 }}>
                                  <IconButton
                                    aria-label="expand row"
                                    size="small"
                                    onClick={() =>
                                      setOpen(index === open ? -1 : index)
                                    }
                                  >
                                    {index === open ? (
                                      <KeyboardArrowUpIcon />
                                    ) : (
                                      <KeyboardArrowDownIcon />
                                    )}
                                  </IconButton>
                                </TableCell>
                              )}

                              {selectable && (
                                <TableCell
                                  padding={
                                    paddingNoneRows ? "none" : "checkbox"
                                  }
                                  key={`TableCell_selectable_${row[rowId]}`}
                                >
                                  {multiSelect && (
                                    <Checkbox
                                      key={`checkbox_${row[rowId]}`}
                                      checked={isItemSelected}
                                      inputProps={{
                                        "aria-labelledby": labelId,
                                      }}
                                    />
                                  )}
                                </TableCell>
                              )}
                              {columnIds.map((id, indexColumns) => {
                                return (
                                  <TableCell
                                    align={rowAlign[indexColumns]}
                                    key={indexColumns}
                                    padding={
                                      paddingNoneRows ? "none" : "normal"
                                    }
                                  >
                                    {
                                      // Adiciona a formatação
                                      rowFormat[indexColumns] && row
                                        ? rowFormat[indexColumns](row)
                                        : row[id]
                                    }
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                            <TableRow>
                              <TableCell
                                style={{ paddingBottom: 0, paddingTop: 0 }}
                                colSpan={12}
                              >
                                <Collapse
                                  in={index === open}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <Box margin={1}>{row.subitemTable}</Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })
              }
              {/* adiciona o espaço das rows vazias */}
              {emptyRows > 0 && !loading && (
                <TableRow
                  style={{
                    height: (initialSize * emptyRows) / 2,
                  }}
                >
                  <TableCell colSpan={12} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CustomContainer>
        {disabledDefaultPagination ? (
          <> </>
        ) : (
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={apiTotalRows ?? 0}
            rowsPerPage={rowsPerPageDefault}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </CustomTable>
  );
}
